import api from './api';

/** AI 서버(백엔드 호스트) 자원 — 브라우저/접속 PC 아님 */

export interface AiServerGpuItem {
  id?: string;
  index?: number | null;
  name?: string;
  vendor?: string;
  kind?: string; // discrete | integrated | unknown
  source?: string;
  util_percent?: number | null;
  mem_util_percent?: number | null;
  mem_used_mb?: number | null;
  mem_total_mb?: number | null;
  mem_percent?: number | null;
  temperature_c?: number | null;
  driver_version?: string;
}

export interface AiServerDiskPartition {
  path: string;
  fstype?: string;
  percent: number;
  total_gb?: number | null;
  used_gb?: number | null;
  free_gb?: number | null;
}

export interface AiServerThermalSensor {
  id?: string;
  component?: string;
  label?: string;
  celsius?: number | null;
  status?: string;
  source?: string;
  gpu_id?: string;
  gpu_index?: number | null;
}

export interface AiServerThermal {
  available?: boolean;
  cpu_celsius?: number | null;
  cpu_celsius_avg?: number | null;
  gpu_celsius_max?: number | null;
  gpu_celsius_avg?: number | null;
  cpu_status?: string;
  gpu_status?: string;
  sensors?: AiServerThermalSensor[];
}

export interface AiServerNic {
  name: string;
  is_up?: boolean;
  speed_mbps?: number | null;
  bytes_sent?: number | null;
  bytes_recv?: number | null;
  errin?: number | null;
  errout?: number | null;
  dropin?: number | null;
  dropout?: number | null;
}

export interface AiServerNetwork {
  bytes_sent?: number | null;
  bytes_recv?: number | null;
  send_bps?: number | null;
  recv_bps?: number | null;
  send_mbps?: number | null;
  recv_mbps?: number | null;
  send_load_percent?: number | null;
  recv_load_percent?: number | null;
  total_load_percent?: number | null;
  link_ref_mbps?: number | null;
  nics?: AiServerNic[];
}

export interface AiServerResourceCurrent {
  source: string;
  hostname?: string;
  platform?: string;
  sampled_at?: string;
  cpu: {
    percent: number;
    per_core?: number[];
    count_logical?: number | null;
    count_physical?: number | null;
    load_avg_1m?: number | null;
    temp_celsius?: number | null;
    temp_status?: string;
  };
  memory: {
    percent: number;
    total_gb?: number | null;
    used_gb?: number | null;
    available_gb?: number | null;
  };
  disk: {
    path?: string;
    percent: number;
    total_gb?: number | null;
    used_gb?: number | null;
    free_gb?: number | null;
    partitions?: AiServerDiskPartition[];
  };
  gpu: {
    available: boolean;
    count?: number;
    util_avg?: number | null;
    mem_percent_avg?: number | null;
    gpus?: AiServerGpuItem[];
    reason?: string;
  };
  thermal?: AiServerThermal;
  network?: AiServerNetwork;
  process_count?: number;
  uptime_seconds?: number;
}

export interface AiServerAgg {
  avg: number | null;
  max: number | null;
  min: number | null;
  count: number;
}

export interface AiServerResourceStats {
  source: string;
  period: string;
  bucket: string;
  bucket_timezone?: string;
  start: string;
  end: string;
  sample_count: number;
  averages?: Record<string, number | null>;
  summary: {
    cpu: AiServerAgg;
    memory: AiServerAgg;
    disk: AiServerAgg;
    gpu: AiServerAgg;
    gpu_memory: AiServerAgg;
    cpu_temp?: AiServerAgg;
    gpu_temp?: AiServerAgg;
    net_send_mbps?: AiServerAgg;
    net_recv_mbps?: AiServerAgg;
    net_load?: AiServerAgg;
    high_cpu_sample_count: number;
    high_mem_sample_count: number;
    approx_high_cpu_minutes: number;
    approx_high_mem_minutes: number;
  };
  series: Array<{
    bucket: string;
    cpu_avg: number | null;
    mem_avg: number | null;
    disk_avg: number | null;
    gpu_avg: number | null;
    gpu_mem_avg: number | null;
    cpu_temp_avg?: number | null;
    gpu_temp_avg?: number | null;
    net_send_mbps_avg?: number | null;
    net_recv_mbps_avg?: number | null;
    net_load_avg?: number | null;
    sample_count: number;
    gpus?: Record<string, number | null>;
  }>;
  per_gpu?: Array<{
    id: string;
    name: string;
    vendor?: string;
    kind?: string;
    summary: {
      util: AiServerAgg;
      memory: AiServerAgg;
    };
  }>;
}

export async function fetchAiServerResourcesCurrent(): Promise<AiServerResourceCurrent> {
  return api.get<AiServerResourceCurrent>(
    '/api/ai-server-resources/current',
    undefined,
    { useCache: false }
  );
}

export async function fetchAiServerResourcesStats(
  period: 'day' | 'month' = 'day'
): Promise<AiServerResourceStats> {
  return api.get<AiServerResourceStats>(
    '/api/ai-server-resources/stats',
    { period },
    { useCache: false }
  );
}

export interface AiServerSamplesDeleteParams {
  /** YYYY-MM-DD (포함) */
  after?: string;
  /** YYYY-MM-DD (포함) */
  before?: string;
  /** N일보다 오래된 샘플 */
  olderThanDays?: number;
}

export interface AiServerSamplesDeleteResult {
  deleted?: number;
  count?: number;
  after?: string | null;
  before?: string | null;
  older_than_days?: number | null;
}

function samplesDeleteQuery(params: AiServerSamplesDeleteParams): Record<string, string | number> {
  const q: Record<string, string | number> = {};
  if (params.after) q.after = params.after;
  if (params.before) q.before = params.before;
  if (params.olderThanDays != null && Number.isFinite(params.olderThanDays)) {
    q.older_than_days = Math.floor(params.olderThanDays);
  }
  return q;
}

/** 삭제 대상 건수 미리보기 */
export async function countAiServerResourceSamples(
  params: AiServerSamplesDeleteParams
): Promise<AiServerSamplesDeleteResult> {
  return api.get<AiServerSamplesDeleteResult>(
    '/api/ai-server-resources/samples/count',
    samplesDeleteQuery(params),
    { useCache: false }
  );
}

/** 기간 지정 샘플 삭제 (최고관리자) */
export async function deleteAiServerResourceSamples(
  params: AiServerSamplesDeleteParams
): Promise<AiServerSamplesDeleteResult> {
  return api.delete<AiServerSamplesDeleteResult>(
    '/api/ai-server-resources/samples',
    { params: samplesDeleteQuery(params) }
  );
}

/** 통계 파일 다운로드 (CSV/JSON). Blob + 파일명 반환. */
export async function downloadAiServerResourcesExport(
  period: 'day' | 'month' = 'day',
  format: 'csv' | 'json' = 'csv'
): Promise<{ blob: Blob; filename: string }> {
  const { getBearerTokenForApi } = await import('../utils/poolSiteAuth');
  const { API_ORIGIN } = await import('../config');
  const token = getBearerTokenForApi();
  const qs = new URLSearchParams({ period, format });
  const url = `${API_ORIGIN}/api/ai-server-resources/export?${qs.toString()}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `다운로드 실패 (${res.status})`);
  }
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename="?([^";]+)"?/i);
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const filename =
    m?.[1] || `ai_server_resources_${period}_${stamp}.${format === 'json' ? 'json' : 'csv'}`;
  const blob = await res.blob();
  return { blob, filename };
}

export function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export function formatUptime(seconds?: number | null): string {
  if (seconds == null || seconds < 0 || Number.isNaN(seconds)) return '-';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}일 ${h}시간`;
  if (h > 0) return `${h}시간 ${m}분`;
  return `${m}분`;
}

export function formatBytesLabel(n?: number | null): string {
  if (n == null || Number.isNaN(n)) return '-';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = -1;
  do {
    v /= 1024;
    i += 1;
  } while (v >= 1024 && i < units.length - 1);
  return `${v.toFixed(1)} ${units[i]}`;
}

export function formatMbps(n?: number | null): string {
  if (n == null || Number.isNaN(n)) return '-';
  if (n < 0.01) return `${(n * 1000).toFixed(1)} Kbps`;
  return `${n.toFixed(2)} Mbps`;
}

/** 시계열 버킷 → 차트 축 라벨 (서버 로컬 시각 문자열) */
export function formatSeriesBucketLabel(bucket: string, bucketKind?: string): string {
  if (!bucket) return '';
  // "YYYY-MM-DD HH:00" → "HH:00"
  if (bucketKind === 'hour' && bucket.length >= 16) {
    return bucket.slice(11, 16);
  }
  // "YYYY-MM-DD" → "MM-DD"
  if (bucket.length >= 10 && bucket[4] === '-' && bucket[7] === '-') {
    return bucket.slice(5, 10);
  }
  return bucket;
}

export function tempStatusLabel(status?: string): string {
  switch (status) {
    case 'ok':
      return '정상';
    case 'warm':
      return '주의';
    case 'hot':
      return '높음';
    case 'critical':
      return '위험';
    default:
      return '미확인';
  }
}
